import { conditionToNamedBitmap, createOptOutBitmapFilter } from './named-bitmap';
import { normalizeOptOutChannel } from '@/features/dryad';
import { asJsonObject } from './wire-types';
import type { JsonObject, JsonValue, NewsletterRecipientsFilter, SerializedLegacyMessageFilter } from './wire-types';

/**
 * Converts the bare legacy-array wire (`[{ field, operator, type }]` — the
 * `type`-tagged array dialect with '!'-prefix negation, parsed backend-side by
 * services ui_api/conditions/decode.go) into the proto `{ and: [...] }`
 * filter tree.
 *
 * Deep links still write the array dialect into the filters param — the
 * campaign date-trigger "matching people" link is a confirmed producer — and
 * a builder that speaks only the proto grammar would re-emit it as a proto
 * envelope around legacy leaves, a hybrid no producer emits. The Ember app
 * performs the same conversion at the same boundaries while
 * `profiles-migration` rolls out, so each row's output bytes here are a
 * cross-app contract (pinned fixture-for-fixture in
 * normalize-legacy-filter-wire.test.ts).
 *
 * The array form carries no root operator of its own; every producer and both
 * apps hard-code 'and' for it. A `type`-tagged row this surface doesn't model
 * — or whose fields fail their shape guards — converts to `null`: that is
 * Ember's byte contract (its serializer table has no entry for the type, the
 * row survives lodash's flatMap as `undefined`, and JSON encodes that as
 * null). A null row constrains nobody, so boundaries that feed mutations must
 * gate on {@link hasUnconvertedLegacyRows} instead of forwarding the
 * envelope. Rows without a `type` tag never reach Ember's serializer table
 * and carry no null contract; they pass through verbatim.
 */
export function normalizeLegacyFilterWire(wire: JsonValue): JsonValue {
  if (Array.isArray(wire)) {
    return { and: wire.map(convertLegacyRow) };
  }
  return wire;
}

function wrapInNotFilterIf(filter: JsonValue, shouldWrap: boolean): JsonValue {
  if (shouldWrap) {
    return { not: filter };
  }
  return filter;
}

// A wire value an attribute-shaped leaf can carry. Numbers survive verbatim —
// stringifying one would change the encoded bytes.
type AttributeWireValue = string | number;

function attributeWireValue(value: JsonValue): AttributeWireValue | null {
  return typeof value === 'string' || typeof value === 'number' ? value : null;
}

type AttributeShapedRow = {
  field: string;
  operator: string;
  value: AttributeWireValue | null;
  value2: AttributeWireValue | null;
};

function attributeShapedRow(row: JsonObject): AttributeShapedRow | null {
  if (typeof row.field !== 'string' || typeof row.operator !== 'string') {
    return null;
  }
  const value = attributeWireValue(row.value);
  const value2 = attributeWireValue(row.value2);
  if ((row.value != null && value == null) || (row.value2 != null && value2 == null)) {
    return null;
  }
  return { field: row.field, operator: row.operator, value, value2 };
}

type PrunedAttributeLeaf = {
  field?: string;
  value?: AttributeWireValue;
  value2?: AttributeWireValue;
  operator?: string;
};

// The legacy attribute-shaped leaves prune nullish and empty values and keep
// this exact key order (field, value, value2, operator) — the encoded param is
// compared byte-for-byte across apps, so insertion order is part of the
// contract.
function prunedAttributeLeaf(row: AttributeShapedRow): PrunedAttributeLeaf {
  const pruned: PrunedAttributeLeaf = {};
  if (row.field !== '') {
    pruned.field = row.field;
  }
  if (row.value != null && row.value !== '') {
    pruned.value = row.value;
  }
  if (row.value2 != null && row.value2 !== '') {
    pruned.value2 = row.value2;
  }
  const wireOperator = row.operator.replace('!', '');
  if (wireOperator !== '') {
    pruned.operator = wireOperator;
  }
  return pruned;
}

function convertAttributeShapedRow(key: string, untrustedRow: JsonObject): JsonValue | null {
  const row = attributeShapedRow(untrustedRow);
  if (row == null) {
    return null;
  }
  return wrapInNotFilterIf({ [key]: prunedAttributeLeaf(row) }, row.operator.startsWith('!'));
}

function convertSegmentsRow(row: JsonObject): JsonValue | null {
  if (!Array.isArray(row.segments)) {
    return null;
  }
  const leaves = row.segments.map((id) =>
    typeof id === 'number' || typeof id === 'string' ? { segment: { id } } : null,
  );
  if (leaves.some((leaf) => leaf == null)) {
    return null;
  }
  return wrapInNotFilterIf({ or: leaves }, row.negate === true);
}

function convertMessageRow(row: JsonObject): JsonValue | null {
  // Foreign-event message rows never reach the array wire (they exist only
  // inside the proto round-trip); an unexpected one passes through rather
  // than being re-encoded with the wrong meaning.
  if (row.isForeignEvent === true) {
    return null;
  }
  const event = asJsonObject(row.event);
  if (event == null || typeof event.name !== 'string' || typeof event.type !== 'string') {
    return null;
  }
  const envelope: SerializedLegacyMessageFilter = {
    type: 'message',
    event: {
      name: event.name,
      type: event.type,
      human: event.human === true,
      filters: asJsonObject(event.filters) ?? {},
    },
    inverse: row.inverse === true,
  };
  let bitmap;
  try {
    bitmap = conditionToNamedBitmap(envelope);
  } catch {
    // An event type outside the bitmap grammar has no proto spelling.
    return null;
  }
  return bitmap;
}

function convertNewsletterRecipientsRow(row: JsonObject): JsonValue | null {
  if (typeof row.newsletter_id !== 'number' && typeof row.newsletter_id !== 'string') {
    return null;
  }
  const recipients: NewsletterRecipientsFilter['newsletter_recipients'] = { id: row.newsletter_id };
  if (typeof row.template_id === 'number' || typeof row.template_id === 'string') {
    recipients.templateID = row.template_id;
  } else if (row.template_id != null) {
    return null;
  }
  return wrapInNotFilterIf({ newsletter_recipients: recipients }, row.negate === true);
}

// String ids convert too: Ember's serializer gates only on the type tag and
// copies both ids whatever they are, so a stringly-typed deep link must keep
// its bytes (copied verbatim, no coercion).
function convertSegAttrRow(row: JsonObject): JsonValue | null {
  const segmentId = row.segment_id;
  const campaignId = row.campaign_id;
  if (typeof segmentId !== 'number' && typeof segmentId !== 'string') {
    return null;
  }
  if (typeof campaignId !== 'number' && typeof campaignId !== 'string') {
    return null;
  }
  return { seg_attr: { segment_id: segmentId, campaign_id: campaignId } };
}

// The rows come off a user-editable URL param, so every field read is guarded
// at runtime; a row failing its guard passes through verbatim.
// fallow-ignore-next-line complexity -- one dispatch table over the legacy row type tags; splitting hurts readability
function convertLegacyRow(untrustedRow: JsonValue): JsonValue {
  const row = asJsonObject(untrustedRow);
  if (row == null) {
    return untrustedRow;
  }

  let converted: JsonValue | null = null;
  switch (row.type) {
    case 'attribute':
      converted = convertAttributeShapedRow('attribute', row);
      break;
    case 'device_attribute':
    case 'device_change':
      converted = convertAttributeShapedRow('device_attribute', row);
      break;
    case 'segments_negatable':
      converted = convertSegmentsRow(row);
      break;
    case 'message':
      converted = convertMessageRow(row);
      break;
    case 'newsletter_recipients':
      converted = convertNewsletterRecipientsRow(row);
      break;
    case 'opt_out':
      converted =
        typeof row.phoneNumber === 'string'
          ? wrapInNotFilterIf(
              createOptOutBitmapFilter(
                row.phoneNumber,
                normalizeOptOutChannel(typeof row.channel === 'string' ? row.channel : null),
              ),
              row.negate === true,
            )
          : null;
      break;
    case 'seg_attr':
      converted = convertSegAttrRow(row);
      break;
    default:
      converted = null;
  }
  // The `type` tag decides the fallback (see normalizeLegacyFilterWire's doc):
  // a tagged row that failed to convert becomes null, matching Ember's bytes;
  // an untagged row keeps its original bytes.
  return converted ?? ('type' in row ? null : row);
}

/**
 * True when a normalized envelope carries a null row in its root group — a
 * legacy-array row {@link normalizeLegacyFilterWire} could not model, or a
 * hand-authored null in the user-editable param. A null row is not a
 * constraint, so boundaries that mutate — bulk delete (via
 * `normalizedPeopleFilterEnvelope`) and export (via
 * `exportablePeopleFilters`) — treat the envelope as having no valid filter
 * and refuse, even though the list/URL path forwards the same bytes for
 * cross-app parity.
 */
export function hasUnconvertedLegacyRows(wire: JsonValue): boolean {
  const root = asJsonObject(wire);
  if (root == null) {
    return false;
  }
  const children = Array.isArray(root.and) ? root.and : root.or;
  return Array.isArray(children) && children.some((child) => child === null);
}
